import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Mail, Phone, FileText, Loader2, FolderOpen,
  Upload, Download, Trash2, Plus, DollarSign, Calendar, CreditCard, Paperclip, X, Store, FileCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Inline editable cell
function EditableCell({ value, field, id, type = 'text', onSave, isCurrency, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value ?? ''); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = () => {
    const newVal = type === 'number' ? (draft === '' ? null : parseFloat(draft)) : (draft || null);
    const oldVal = type === 'number' ? (value ?? null) : (value || null);
    if (newVal !== oldVal) onSave(id, field, newVal);
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
  };

  if (editing) {
    return (
      <Input ref={inputRef} type={type} step={type === 'number' ? '0.01' : undefined}
        value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={handleKeyDown} onBlur={save}
        className={`h-7 text-sm px-2 py-0 min-w-[60px] ${className}`}
        data-testid={`inline-edit-${field}-${id}`} />
    );
  }

  const display = isCurrency && value != null
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
    : (value || '-');

  return (
    <span onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className={`cursor-pointer hover:bg-blue-50 hover:text-blue-700 px-1.5 py-0.5 rounded transition-colors inline-block min-w-[30px] ${className}`}
      title="Click to edit" data-testid={`cell-${field}-${id}`}>
      {display}
    </span>
  );
}

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  const [uploading, setUploading] = useState(false);
  const [deleteFileTarget, setDeleteFileTarget] = useState(null);
  const fileInputRef = useRef(null);

  const [contractUploading, setContractUploading] = useState(false);
  const contractInputRef = useRef(null);

  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ date: '', amount: '', method: '', notes: '' });
  const [paymentFile, setPaymentFile] = useState(null);
  const [paymentUploading, setPaymentUploading] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [deletePaymentTarget, setDeletePaymentTarget] = useState(null);
  const paymentFileRef = useRef(null);

  useEffect(() => { fetchClient(); }, [clientId]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients/${clientId}`, { withCredentials: true });
      setClient(response.data);
    } catch {
      toast.error('Failed to load client');
      navigate('/clients');
    } finally { setLoading(false); }
  };

  // Inline save for client fields
  const handleClientSave = async (id, field, value) => {
    try {
      await axios.put(`${API_URL}/api/clients/${id}`, { [field]: value }, { withCredentials: true });
      setClient(prev => ({ ...prev, [field]: value }));
    } catch { toast.error('Failed to save'); fetchClient(); }
  };

  // Inline save for payment fields
  const handlePaymentSave = async (paymentId, field, value) => {
    try {
      await axios.put(`${API_URL}/api/payments/${paymentId}`, { [field]: value }, { withCredentials: true });
      fetchClient(); // Refresh to get updated balance
    } catch { toast.error('Failed to save'); fetchClient(); }
  };

  // --- File handlers ---
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (10MB max)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/files/upload?company_id=${client.company_id}&client_id=${clientId}`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('File uploaded'); fetchClient();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteFile = async () => {
    if (!deleteFileTarget) return;
    try {
      await axios.delete(`${API_URL}/api/files/${deleteFileTarget.id}`, { withCredentials: true });
      toast.success('File deleted'); setDeleteFileTarget(null); fetchClient();
    } catch { toast.error('Failed to delete file'); }
  };

  const handleContractUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (10MB max)'); return; }
    setContractUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/files/upload?company_id=${client.company_id}&client_id=${clientId}&tag=contract`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Contract uploaded'); fetchClient();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setContractUploading(false); e.target.value = ''; }
  };

  // --- Payment handlers ---
  const openAddPayment = () => {
    setPaymentForm({ date: new Date().toISOString().split('T')[0], amount: '', method: client.payment_method || '', notes: '' });
    setPaymentFile(null);
    setIsPaymentOpen(true);
  };

  const handlePaymentFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (10MB max)'); return; }
    setPaymentUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/files/upload?company_id=${client.company_id}&client_id=${clientId}`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setPaymentFile(res.data); toast.success('Invoice attached');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setPaymentUploading(false); if (paymentFileRef.current) paymentFileRef.current.value = ''; }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.date) { toast.error('Date is required'); return; }
    if (!paymentForm.amount || isNaN(paymentForm.amount)) { toast.error('Valid amount required'); return; }
    setSubmittingPayment(true);
    try {
      await axios.post(`${API_URL}/api/payments`, {
        client_id: clientId, date: paymentForm.date, amount: parseFloat(paymentForm.amount),
        method: paymentForm.method || null, notes: paymentForm.notes || null, file_id: paymentFile?.id || null
      }, { withCredentials: true });
      toast.success('Payment recorded'); setIsPaymentOpen(false); fetchClient();
    } catch { toast.error('Failed to add payment'); }
    finally { setSubmittingPayment(false); }
  };

  const handleDeletePayment = async () => {
    if (!deletePaymentTarget) return;
    try {
      await axios.delete(`${API_URL}/api/payments/${deletePaymentTarget.id}`, { withCredentials: true });
      toast.success('Payment deleted'); setDeletePaymentTarget(null); fetchClient();
    } catch { toast.error('Failed to delete payment'); }
  };

  // Attach invoice to existing payment
  const [attachingPaymentId, setAttachingPaymentId] = useState(null);
  const invoiceInputRef = useRef(null);

  // Inline file rename
  const [renamingFileId, setRenamingFileId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');

  const startRenameFile = (file) => {
    setRenamingFileId(file.id);
    setRenameDraft(file.filename || '');
  };
  const cancelRenameFile = () => { setRenamingFileId(null); setRenameDraft(''); };
  const saveRenameFile = async () => {
    if (!renamingFileId) return;
    const newName = renameDraft.trim();
    if (!newName) { toast.error('Name is required'); return; }
    try {
      await axios.put(`${API_URL}/api/files/${renamingFileId}/rename`, { filename: newName }, { withCredentials: true });
      toast.success('File renamed');
      setRenamingFileId(null);
      setRenameDraft('');
      fetchClient();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rename failed'); }
  };

  const handleAttachInvoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !attachingPaymentId) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (10MB max)'); return; }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/files/upload?company_id=${client.company_id}&client_id=${clientId}`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      await axios.put(`${API_URL}/api/payments/${attachingPaymentId}`, { file_id: res.data.id }, { withCredentials: true });
      toast.success('Invoice attached');
      fetchClient();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to attach'); }
    finally { setAttachingPaymentId(null); if (invoiceInputRef.current) invoiceInputRef.current.value = ''; }
  };

  const triggerInvoiceAttach = (paymentId) => {
    setAttachingPaymentId(paymentId);
    setTimeout(() => invoiceInputRef.current?.click(), 0);
  };

  const formatCurrency = (v) => v != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v) : '-';
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const k = 1024; const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return ' (' + parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i] + ')';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!client) return null;

  // Inline rename UI for file rows
  const renderFilename = (file) => {
    if (renamingFileId === file.id) {
      return (
        <div className="flex items-center gap-1 w-full">
          <Input
            autoFocus
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveRenameFile();
              if (e.key === 'Escape') cancelRenameFile();
            }}
            onBlur={saveRenameFile}
            className="h-7 text-sm px-2 py-0"
            data-testid={`rename-file-input-${file.id}`}
          />
        </div>
      );
    }
    return (
      <p
        className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-blue-700 hover:underline"
        onClick={(e) => { e.stopPropagation(); startRenameFile(file); }}
        title="Click to rename"
        data-testid={`file-name-${file.id}`}
      >
        {file.filename}
      </p>
    );
  };

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <input ref={invoiceInputRef} type="file" className="hidden" onChange={handleAttachInvoice} data-testid="invoice-attach-input" />
      <button onClick={() => navigate('/clients')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4" /><span>Back</span>
      </button>

      {/* Client Header - All Editable */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="mb-4">
          <EditableCell value={client.name} field="name" id={clientId} onSave={handleClientSave} className="text-2xl font-semibold text-gray-900" />
          <div className="flex items-center gap-1.5 mt-1">
            <Store className="w-4 h-4 text-gray-400" />
            <EditableCell value={client.store} field="store" id={clientId} onSave={handleClientSave} className="text-gray-500" />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1">
            <p className="text-xs text-gray-400 uppercase">Amount</p>
            <EditableCell value={client.current_amount} field="current_amount" id={clientId} type="number" onSave={handleClientSave} isCurrency className="text-lg font-semibold text-gray-900" />
          </div>
          <div className="space-y-1">
            <p className="text-xs text-gray-400 uppercase">Balance Due</p>
            <EditableCell value={client.balance_due} field="balance_due" id={clientId} type="number" onSave={handleClientSave} isCurrency className={`text-lg font-semibold ${(client.balance_due || 0) > 0 ? 'text-red-600' : 'text-blue-600'}`} />
          </div>
          <div className="space-y-1">
            <p className="text-xs text-gray-400 uppercase">Total Paid</p>
            <p className="text-lg font-semibold text-blue-600 px-1.5">{formatCurrency(client.total_paid)}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-gray-400 uppercase">Terms</p>
            <EditableCell value={client.terms} field="terms" id={clientId} onSave={handleClientSave} className="text-lg font-medium text-gray-700" />
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="space-y-1">
            <p className="text-xs text-gray-400 uppercase">Description</p>
            <EditableCell value={client.description} field="description" id={clientId} onSave={handleClientSave} className="text-gray-600" />
          </div>
          <div className="space-y-1">
            <p className="text-xs text-gray-400 uppercase">Payment Method</p>
            <EditableCell value={client.payment_method} field="payment_method" id={clientId} onSave={handleClientSave} className="text-gray-600" />
          </div>
          <div className="space-y-1">
            <p className="text-xs text-gray-400 uppercase">Start Date</p>
            <EditableCell value={client.start_date} field="start_date" id={clientId} type="date" onSave={handleClientSave} className="text-gray-600" />
          </div>
          <div className="space-y-1">
            <p className="text-xs text-gray-400 uppercase">Email</p>
            <EditableCell value={client.email} field="email" id={clientId} onSave={handleClientSave} className="text-gray-600" />
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div className="space-y-1">
            <p className="text-xs text-gray-400 uppercase">Phone</p>
            <EditableCell value={client.phone} field="phone" id={clientId} onSave={handleClientSave} className="text-gray-600" />
          </div>
          <div className="space-y-1">
            <p className="text-xs text-gray-400 uppercase">Notes</p>
            <EditableCell value={client.notes} field="notes" id={clientId} onSave={handleClientSave} className="text-gray-500" />
          </div>
        </div>
      </div>

      {/* Contracts Section */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-medium text-gray-900 flex items-center gap-2"><FileCheck className="w-4 h-4" />Contracts ({client.contracts?.length || 0})</h2>
          <div>
            <input ref={contractInputRef} type="file" className="hidden" onChange={handleContractUpload} data-testid="contract-upload-input" />
            <Button variant="outline" size="sm" onClick={() => contractInputRef.current?.click()} disabled={contractUploading} data-testid="upload-contract-btn">
              {contractUploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              {contractUploading ? 'Uploading...' : 'Upload Contract'}
            </Button>
          </div>
        </div>
        <div className="p-4">
          {client.contracts?.length > 0 ? (
            <div className="space-y-2">
              {client.contracts.map((file) => (
                <div key={file.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg group" data-testid={`contract-file-${file.id}`}>
                  <FileCheck className="w-4 h-4 text-indigo-500" />
                  <div className="flex-1 min-w-0">
                    {renderFilename(file)}
                    <p className="text-xs text-gray-500">{new Date(file.created_at).toLocaleDateString()}{formatFileSize(file.size)}</p>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                    <a href={`${API_URL}/api/files/${file.id}/download`} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="icon" className="h-7 w-7"><Download className="w-3.5 h-3.5" /></Button></a>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => setDeleteFileTarget(file)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileCheck className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 mb-3">No contracts uploaded yet</p>
              <Button variant="outline" size="sm" onClick={() => contractInputRef.current?.click()}><Upload className="w-4 h-4 mr-1" />Upload Contract</Button>
            </div>
          )}
        </div>
      </div>

      {/* Payments Section - Inline Editable */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-medium text-gray-900 flex items-center gap-2"><DollarSign className="w-4 h-4" />Payments ({client.payments?.length || 0})</h2>
          <Button size="sm" onClick={openAddPayment} data-testid="add-payment-btn"><Plus className="w-4 h-4 mr-1" />Add Payment</Button>
        </div>
        <div className="p-4">
          {client.payments?.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  <th className="pb-2 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {client.payments.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50" data-testid={`payment-row-${p.id}`}>
                    <td className="py-2 text-sm">
                      <EditableCell value={p.date} field="date" id={p.id} type="date" onSave={handlePaymentSave} className="font-medium text-gray-900" />
                    </td>
                    <td className="py-2 text-sm text-right">
                      <EditableCell value={p.amount} field="amount" id={p.id} type="number" onSave={handlePaymentSave} isCurrency className="font-medium text-blue-600" />
                    </td>
                    <td className="py-2 text-sm">
                      <EditableCell value={p.method} field="method" id={p.id} onSave={handlePaymentSave} className="text-gray-600" />
                    </td>
                    <td className="py-2">
                      {p.file_info ? (
                        <div className="flex items-center gap-1">
                          <a href={`${API_URL}/api/files/${p.file_id}/download`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm">
                            <Paperclip className="w-3.5 h-3.5" /><span className="truncate max-w-[100px]">{p.file_info.filename}</span>
                          </a>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-gray-600" onClick={() => triggerInvoiceAttach(p.id)} title="Replace invoice">
                            <Upload className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button variant="ghost" size="sm" className="text-gray-400 hover:text-blue-600 h-7 px-2" onClick={() => triggerInvoiceAttach(p.id)} data-testid={`attach-invoice-${p.id}`}>
                          <Upload className="w-3.5 h-3.5 mr-1" />Attach
                        </Button>
                      )}
                    </td>
                    <td className="py-2 text-sm">
                      <EditableCell value={p.notes} field="notes" id={p.id} onSave={handlePaymentSave} className="text-gray-500" />
                    </td>
                    <td className="py-2">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => setDeletePaymentTarget(p)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8">
              <DollarSign className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 mb-3">No payments recorded yet</p>
              <Button variant="outline" size="sm" onClick={openAddPayment}><Plus className="w-4 h-4 mr-1" />Add Payment</Button>
            </div>
          )}
        </div>
      </div>

      {/* Files / Invoices Section */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-medium text-gray-900 flex items-center gap-2"><FolderOpen className="w-4 h-4" />Files & Invoices ({client.files?.length || 0})</h2>
          <div>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} data-testid="client-file-upload-input" />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="client-upload-file-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </div>
        <div className="p-4">
          {client.files?.length > 0 ? (
            <div className="space-y-2">
              {client.files.map((file) => (
                <div key={file.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg group" data-testid={`client-file-${file.id}`}>
                  <FileText className="w-4 h-4 text-gray-400" />
                  <div className="flex-1 min-w-0">
                    {renderFilename(file)}
                    <p className="text-xs text-gray-500">{new Date(file.created_at).toLocaleDateString()}{formatFileSize(file.size)}</p>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                    <a href={`${API_URL}/api/files/${file.id}/download`} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="icon" className="h-7 w-7"><Download className="w-3.5 h-3.5" /></Button></a>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => setDeleteFileTarget(file)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FolderOpen className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 mb-3">No files yet</p>
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}><Upload className="w-4 h-4 mr-1" />Upload File</Button>
            </div>
          )}
        </div>
      </div>

      {/* Add Payment Dialog */}
      <Dialog open={isPaymentOpen} onOpenChange={setIsPaymentOpen}>
        <DialogContent data-testid="add-payment-dialog">
          <DialogHeader><DialogTitle>Add Payment</DialogTitle><DialogDescription>Record a payment for {client.name}</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Date *</Label><Input type="date" value={paymentForm.date} onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })} data-testid="payment-date-input" /></div>
              <div className="space-y-2"><Label>Amount *</Label><Input type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} placeholder="0.00" data-testid="payment-amount-input" /></div>
            </div>
            <div className="space-y-2"><Label>Method</Label><Input value={paymentForm.method} onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })} placeholder="e.g. Zelle, Wire, Check" data-testid="payment-method-input" /></div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} placeholder="Additional notes..." /></div>
            <div className="space-y-2">
              <Label>Attach Invoice</Label>
              {paymentFile ? (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                  <Paperclip className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700 flex-1 truncate">{paymentFile.filename}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPaymentFile(null)}><X className="w-3.5 h-3.5" /></Button>
                </div>
              ) : (
                <div>
                  <input ref={paymentFileRef} type="file" className="hidden" onChange={handlePaymentFileUpload} data-testid="payment-invoice-input" />
                  <Button variant="outline" size="sm" onClick={() => paymentFileRef.current?.click()} disabled={paymentUploading} data-testid="payment-attach-btn">
                    {paymentUploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                    {paymentUploading ? 'Uploading...' : 'Attach Invoice'}
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPaymentOpen(false)}>Cancel</Button>
            <Button onClick={handleAddPayment} disabled={submittingPayment} data-testid="save-payment-btn">
              {submittingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete File Dialog */}
      <AlertDialog open={!!deleteFileTarget} onOpenChange={(o) => !o && setDeleteFileTarget(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete File</AlertDialogTitle><AlertDialogDescription>Delete "{deleteFileTarget?.filename}"?</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteFile} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      {/* Delete Payment Dialog */}
      <AlertDialog open={!!deletePaymentTarget} onOpenChange={(o) => !o && setDeletePaymentTarget(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Payment</AlertDialogTitle><AlertDialogDescription>Delete {deletePaymentTarget ? formatCurrency(deletePaymentTarget.amount) : ''} payment from {deletePaymentTarget?.date}? The balance will be restored.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeletePayment} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
